/* P1 PRO v18: visible-time autoplay, accessible manual navigation, one active panel.
 * The plot uses monotone time coordinates. Dots follow both paths at the SAME time.
 * No network requests or customer calculations are performed by this component. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('zv-p1-advice')) return;
  class ZinVoltP1Advice extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.tabs = Array.from(this.querySelectorAll('[data-advice-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-advice-panel]'));
      this.viewer = this.querySelector('.p1-advice__viewer');
      this.nextButton = this.querySelector('[data-advice-next]');
      this.nextLabel = this.querySelector('[data-advice-next-label]');
      this.counter = this.querySelector('[data-advice-count]');
      this.status = this.querySelector('[data-advice-status]');
      this.steps = this.querySelector('.p1-advice__steps');
      this.controls = this.querySelector('[data-advice-controls]');
      this.reasons = this.querySelector('[data-advice-reasons]');
      this.progress = Array.from(this.querySelectorAll('.p1-advice__progress i'));
      if (this.tabs.length !== 4 || this.panels.length !== 4 || !this.viewer || !this.nextButton || !this.controls) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 899px)');
      this.index = 0;
      this.inView = false;
      this.autoplayRequested = !this.motion.matches;
      this.timer = null;
      this.timerStarted = 0;
      this.remaining = 9000;
      this.frame = null;
      this.graphElapsed = 0;
      this.graphLast = 0;
      this.reasonsTouched = false;
      this.curves = ['import', 'export'].map(name => {
        const path = this.querySelector(`[data-advice-curve="${name}"]`);
        const dot = this.querySelector(`[data-advice-dot="${name}"]`);
        if (!path || !dot || typeof path.getTotalLength !== 'function') return null;
        const length = path.getTotalLength();
        const points = Array.from({ length: 301 }, (_, i) => path.getPointAtLength(length * i / 300));
        return { dot, points };
      }).filter(Boolean);
      this.steps.setAttribute('role', 'tablist');
      this.setResponsiveState();
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.removeAttribute('aria-pressed');
        tab.addEventListener('click', () => {
          this.stopAutoplay();
          this.show(i, true);
        }, { signal });
        tab.addEventListener('keydown', event => this.onKey(event, i), { signal });
      });
      this.panels.forEach(panel => panel.setAttribute('role', 'tabpanel'));
      this.nextButton.addEventListener('click', () => {
        if (this.index === 3) {
          this.pauseTimer();
          this.autoplayRequested = !this.motion.matches;
          this.graphElapsed = 0;
          this.show(0, true);
          this.updatePlayback();
        } else {
          this.stopAutoplay();
          this.show(this.index + 1, true);
        }
      }, { signal });
      this.reasons?.querySelector('summary')?.addEventListener('click', () => {
        this.reasonsTouched = true;
      }, { signal });
      this.panels.forEach(panel => panel.addEventListener('focusin', () => this.stopAutoplay(), { signal }));
      document.addEventListener('visibilitychange', () => this.updatePlayback(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.stopAutoplay();
        this.updatePlayback();
      }, { signal });
      this.mobile.addEventListener('change', () => this.setResponsiveState(), { signal });
      window.addEventListener('resize', () => this.setResponsiveState(), { signal, passive: true });
      this.setAttribute('data-enhanced', '');
      this.controls.hidden = false;
      this.show(0, false);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting && entry.intersectionRatio >= .35;
          this.updatePlayback();
        }, { threshold: [0, .35, .6], rootMargin: '-70px 0px -35px 0px' });
        this.observer.observe(this.viewer);
      } else {
        this.inView = true;
        this.updatePlayback();
      }
    }
    setResponsiveState() {
      this.steps.setAttribute('aria-orientation', this.mobile.matches ? 'horizontal' : 'vertical');
      if (this.reasons && !this.reasonsTouched) this.reasons.open = !this.mobile.matches && window.innerHeight >= 740;
    }
    onKey(event, index) {
      let target;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % 4;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index + 3) % 4;
      else if (event.key === 'Home') target = 0;
      else if (event.key === 'End') target = 3;
      else return;
      event.preventDefault();
      this.stopAutoplay();
      this.tabs[target].focus({ preventScroll: true });
      this.show(target, true);
    }
    show(index, announce = false) {
      if (!Number.isInteger(index) || index < 0 || index > 3) return;
      this.pauseTimer();
      this.index = index;
      this.dataset.step = String(index);
      this.remaining = index === 1 ? 10000 : 9000;
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        panel.inert = i !== index;
        panel.setAttribute('aria-hidden', String(i !== index));
        panel.classList.remove('is-animating');
      });
      if (!this.motion.matches) this.panels[index].classList.add('is-animating');
      this.progress.forEach((item, i) => item.classList.toggle('is-current', i === index));
      if (this.counter) this.counter.textContent = String(index + 1);
      const label = index === 3 ? window.ZinVoltI18n.t("view_again_935287") : window.ZinVoltI18n.t("next_step_87b8bc");
      if (this.nextLabel) this.nextLabel.textContent = label;
      this.nextButton.setAttribute('aria-label', label);
      if (announce && this.status) this.status.textContent = window.ZinVoltI18n.t("step_current_of_4_title_016e25", {current:index + 1,title:this.tabs[index].querySelector('strong').textContent});
      if (index === 3) this.autoplayRequested = false;
      if (index === 0) this.graphElapsed = 0;
      this.updatePlayback();
    }
    pauseTimer() {
      if (this.timer !== null) {
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.timerStarted));
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }
    stopAutoplay() {
      this.autoplayRequested = false;
      this.pauseTimer();
    }
    updatePlayback() {
      const visible = this.isConnected && this.inView && !document.hidden && !this.motion.matches;
      if (visible && this.autoplayRequested && this.index < 3) {
        if (this.timer === null) {
          this.timerStarted = performance.now();
          this.timer = window.setTimeout(() => {
            this.timer = null;
            if (this.inView && !document.hidden && this.autoplayRequested) this.show(this.index + 1);
          }, Math.max(100, this.remaining));
        }
      } else this.pauseTimer();
      if (visible && this.index === 0) this.startGraph();
      else this.stopGraph();
    }
    startGraph() {
      if (this.frame !== null || !this.curves.length) return;
      this.graphLast = 0;
      this.curves.forEach(({ dot }) => { dot.hidden = false; });
      const tick = now => {
        this.frame = null;
        if (!this.isConnected || !this.inView || document.hidden || this.motion.matches || this.index !== 0) {
          this.stopGraph();
          return;
        }
        if (this.graphLast) this.graphElapsed += Math.min(now - this.graphLast, 80);
        this.graphLast = now;
        const t = (this.graphElapsed % 8000) / 8000;
        const x = 24 + 752 * t;
        this.curves.forEach(({ dot, points }) => {
          let low = 0, high = points.length - 1;
          while (high - low > 1) {
            const mid = (low + high) >> 1;
            if (points[mid].x < x) low = mid; else high = mid;
          }
          const a = points[low], b = points[high];
          const ratio = (x - a.x) / Math.max(.0001, b.x - a.x);
          const y = a.y + (b.y - a.y) * ratio;
          dot.style.left = `${x / 8}%`;
          dot.style.top = `${y / 2.3}%`;
        });
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    stopGraph() {
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      this.graphLast = 0;
      this.curves?.forEach(({ dot }) => { dot.hidden = true; });
    }
    disconnectedCallback() {
      this.pauseTimer();
      this.stopGraph();
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.observer = null;
    }
  }
  customElements.define('zv-p1-advice', ZinVoltP1Advice);
})();
